import { Input as AntInput } from 'antd';
import React, {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { InputNumber } from 'components/atoms/input';
import { Text03 } from 'components/styles';

interface EditableCellProps {
  editable: boolean;
  children: React.ReactNode;
  dataIndex: string;
  record;
  update: (record) => void;
}

const Input = styled(InputNumber)`
  background: transparent;
  border-bottom-color: transparent;
  padding: 0 var(--spacing-03);

  &,
  && input {
    height: 24px;
    min-width: initial;
  }

  &.ant-input-number-disabled input {
    ${Text03};
    cursor: unset;
    background: transparent;
  }
`;

const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
  e.preventDefault();

  e.currentTarget.blur();
};
const formatter = (value?: string | number) => `${value}d`;
const parser = (value?: string) => (value || '').replace('d', '');

export const EditableCell: FC<EditableCellProps> = ({
  editable,
  children,
  dataIndex,
  record,
  update,
  ...restProps
}) => {
  const [value, setValue] = useState<number>();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<AntInput>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (record) {
      setValue(record[dataIndex]);
    }
  }, [dataIndex, record]);

  const save = useCallback(() => {
    setEditing(false);
    update({ ...record, [dataIndex]: value });
  }, [dataIndex, record, update, value]);

  const onBlur = useCallback(() => save(), [save]);

  const onChange = useCallback((v) => {
    setValue(v || 1);
  }, []);

  if (record?.data.isNew) {
    return <td />;
  }

  if (!editable) {
    return <td {...restProps}>{children}</td>;
  }

  return (
    <td {...restProps}>
      <Input
        defaultValue={1}
        disabled={
          record.children.filter((child) => !child.data.isNew).length > 0
        }
        formatter={formatter}
        min={1}
        parser={parser}
        ref={inputRef}
        size="small"
        step={1}
        value={value}
        onChange={onChange}
        onPressEnter={onEnter}
        onBlur={onBlur}
      />
    </td>
  );
};
